import { usePersonLabels } from "@/presentation/hooks/usePersonLabels";
import { ShellCommentActor } from "./ShellCommentActor";
import { useTranslation } from "react-i18next";
import {
  Buildings,
  Calculator,
  CheckSquare,
  ChatCircle,
  FileText,
  Note,
  Receipt,
  Square,
  Trash,
  Users,
  Wallet,
} from "@phosphor-icons/react";
import { Button, NotificationActions, NotificationEntityIcon, NotificationRow } from "@hulahoop/ui";
import { IconTooltip } from "@/components/shared/overlays/IconTooltip";
import type { Notification, RelatedEntityTypeValue } from "@/domain/notification";
import { getRelativeTime } from "@/utils/dateFormatters";
import type { ShellInboxState } from "./useShellInbox";

/**
 * The icon a notification's record line shows. Every entity that has a section
 * in the sidebar wears that section's icon, so the inbox and the sidebar name
 * the same thing the same way — `__tests__/ShellInboxItem.test.tsx` pins the
 * pairs against `shellNavigation.ts`.
 */
export const COLLECTION_ICONS: Record<RelatedEntityTypeValue, typeof Buildings> = {
  note: Note,
  comment_thread: ChatCircle,
  beneficiary: Buildings,
  project: Buildings,
  investment: Wallet,
  contract: Receipt,
  // Legacy alias of `contract` (see notificationRouting).
  document: Receipt,
  fiscal_quota: Calculator,
  investor: Users,
  // A proposal lives inside its investor, so it wears that section's icon.
  investment_proposal: Users,
  // A team invitation has no record of its own; it lands on the organization.
  organization: Buildings,
};

const ENTITY_ICONS = {
  note_mentioned: Note,
  comment_mentioned: ChatCircle,
  comment_replied: ChatCircle,
  comment_resolved: ChatCircle,
  system: FileText,
  document: Receipt,
  investment: Wallet,
  project: Buildings,
};

export function ShellInboxItem({
  notification,
  inbox,
}: {
  notification: Notification;
  inbox: ShellInboxState;
}) {
  const { t, i18n } = useTranslation();
  const personLabels = usePersonLabels();
  const collaboration = notification.related_entity_type === "comment_thread";
  const actorName = notification.related_context?.actor_name ?? "";
  const subjectType = notification.related_context?.subject_type;
  const subjectIcons: Record<string, typeof Buildings> = {
    beneficiary: Buildings,
    investor: Users,
    investment: Wallet,
    document: Receipt,
    fiscal_quota: Calculator,
    project: Buildings,
    classification: FileText,
    template: FileText,
  };
  const read = notification.is_read;
  const Entity = ENTITY_ICONS[notification.type];
  const Collection = collaboration
    ? (subjectIcons[subjectType ?? ""] ?? ChatCircle)
    : notification.related_entity_type
      ? COLLECTION_ICONS[notification.related_entity_type]
      : null;
  return (
    <>
      <NotificationRow
        leading={
          collaboration ? (
            <ShellCommentActor
              name={actorName}
              avatar={notification.related_context?.actor_avatar}
              type={notification.type}
              labels={personLabels}
            />
          ) : (
            <NotificationEntityIcon>
              <Entity />
            </NotificationEntityIcon>
          )
        }
        title={collaboration ? actorName : notification.title}
        srLabel={collaboration ? t(`notifications.${notification.type}`) : undefined}
        time={getRelativeTime(notification.created_at, t, i18n.language)}
        unread={!read}
        record={
          Collection && notification.body
            ? {
                icon: <Collection aria-hidden />,
                label: collaboration
                  ? notification.related_context?.subject_label
                  : notification.body,
              }
            : undefined
        }
        excerpt={collaboration || !Collection ? notification.body : undefined}
        onOpen={() => inbox.handleOpen(notification)}
      />
      <NotificationActions>
        <IconTooltip
          label={read ? t("notifications.mark_unread_short") : t("notifications.mark_read_short")}
        >
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={inbox.isBusy}
            aria-label={read ? t("notifications.mark_as_unread") : t("notifications.mark_as_read")}
            onClick={() => void inbox.handleToggleRead(notification)}
          >
            {read ? <Square /> : <CheckSquare />}
          </Button>
        </IconTooltip>
        <IconTooltip label={t("notifications.delete")}>
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={inbox.isBusy}
            aria-label={t("notifications.delete_one")}
            onClick={() => inbox.handleDelete(notification)}
          >
            <Trash />
          </Button>
        </IconTooltip>
      </NotificationActions>
    </>
  );
}
